// PWA auto-update prompt.
//
// vite-plugin-pwa exposes `virtual:pwa-register/react` which gives a
// `useRegisterSW` hook that tells us when a new service worker is waiting.
// We render a small toast in the bottom-right with a Reload button that
// activates the new version immediately.
//
// This works on installed PWAs (phone home screen, desktop standalone)
// AND in regular browser tabs — on every reload the SW checks for a
// newer bundle and surfaces this prompt. No more "I deployed but nobody
// sees the new version".

import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

export default function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      // Re-check for an update when the user returns to the tab and once an hour.
      if (r) {
        const onFocus = () => r.update().catch(() => {});
        document.addEventListener('visibilitychange', onFocus);
        const t = setInterval(onFocus, 60 * 60 * 1000);
        return () => {
          document.removeEventListener('visibilitychange', onFocus);
          clearInterval(t);
        };
      }
    },
    onRegisterError(err) {
      console.warn('[PWA] SW registration error', err);
    }
  });

  // Auto-dismiss the offline-ready toast after 4s.
  useEffect(() => {
    if (!offlineReady) return;
    const t = setTimeout(() => setOfflineReady(false), 4000);
    return () => clearTimeout(t);
  }, [offlineReady, setOfflineReady]);

  if (!needRefresh && !offlineReady) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {needRefresh && (
        <div className="bg-b1s-600 text-white rounded-xl shadow-lg p-4 mb-2">
          <div className="font-semibold text-sm mb-1">A new version of B1S CRM is available</div>
          <div className="text-xs text-b1s-50/90 mb-3">
            Reload to get the latest features and fixes. Your unsaved work won't be lost.
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setNeedRefresh(false)}
              className="text-xs px-3 py-1.5 rounded-md text-b1s-50/90 hover:bg-b1s-700"
            >
              Later
            </button>
            <button
              onClick={() => updateServiceWorker(true)}
              className="text-xs px-3 py-1.5 rounded-md bg-white text-b1s-700 font-semibold hover:bg-b1s-50"
            >
              Reload now
            </button>
          </div>
        </div>
      )}
      {offlineReady && (
        <div className="bg-slate-800 text-white rounded-xl shadow-lg p-3 text-xs">
          <span className="font-semibold">B1S CRM is ready to work offline.</span>
          {' '}Your work will sync when you reconnect.
        </div>
      )}
    </div>
  );
}
